import { useEffect, useMemo, useRef, useState } from 'react';
import {
  computeCutInsights,
  fmtMMSS,
  totalTargetSec,
  pickTip,
  getExperienceLevel,
} from '@busy/core';
import {
  useClientStats,
  useClientTiers,
  useClients,
  useCurrentUserProfile,
  useLatestClientPhotoUrls,
  useRecentVisits,
  type VisitRow,
} from '@busy/api';
import { Avatar, ModalCloseButton } from '@busy/ui';
import {
  getPhaseElapsedSec,
  getTotalElapsedSec,
  useCutTimer,
} from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import { pulseHaptic } from '../../lib/haptics';
import { AddonPicker } from './AddonPicker';
import { ClientDetailModal } from '../ClientDetailModal';

// Rotates the phase tip every 5 minutes within a phase.
const TIP_CYCLE_SEC = 300;

export function LiveTimer() {
  const cutType = useCutTimer((s) => s.cutType);
  const client = useCutTimer((s) => s.client);
  const photoUrls = useLatestClientPhotoUrls();
  const currentPhaseIdx = useCutTimer((s) => s.currentPhaseIdx);
  const paused = useCutTimer((s) => s.paused);
  const addedAddons = useCutTimer((s) => s.addedAddons);
  const switchToPhase = useCutTimer((s) => s.switchToPhase);
  const removeAddon = useCutTimer((s) => s.removeAddon);
  const togglePause = useCutTimer((s) => (s.paused ? s.resume : s.pause));
  const endCut = useCutTimer((s) => s.endCut);
  const closeModal = useCutTimer((s) => s.closeModal);
  const tick = useCutTimer((s) => s.tick);

  const [showAddonPicker, setShowAddonPicker] = useState(false);
  const [tipDismissed, setTipDismissed] = useState(false);
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const profile = useCurrentUserProfile();
  const experienceLevel = getExperienceLevel(profile.data?.years_cutting ?? null);
  const t = useT();
  const language = useUI((s) => s.language);

  // Read full state once per render via a snapshot — components read individual slices for
  // re-render efficiency, but the compute helpers need the whole state shape.
  const stateSnapshot = useCutTimer.getState();

  // 1Hz ticker — bumps tickCount in the store, which triggers a re-render via the snapshot read.
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => tick(), 1000);
    return () => clearInterval(id);
  }, [paused, tick]);

  // Read tickCount so this component re-renders every tick. Store is the source of truth.
  useCutTimer((s) => s.tickCount);

  if (!cutType) return null;

  const phase = cutType.phases[currentPhaseIdx];
  const phaseElapsed = getPhaseElapsedSec(stateSnapshot, currentPhaseIdx);
  const phaseTarget = phase.sec;
  const totalElapsed = getTotalElapsedSec(stateSnapshot);
  const totalTarget = totalTargetSec(cutType.phases as never[]);

  // Tip rotation: phase change resets cycle, then every 5 min within a phase advances.
  // Experience level shifts the tone — new barbers get more teaching tips, masters get
  // more strategic / business tips, mid tier sticks with the shared coaching baseline.
  const tipCycle = Math.floor(phaseElapsed / TIP_CYCLE_SEC);
  const tip = pickTip(phase.cat, tipCycle, experienceLevel, language);
  const phaseName = phase.nameKey ? t(phase.nameKey) : phase.name;
  const cutName = cutType.nameKey ? t(cutType.nameKey) : cutType.name;

  // Reset tip-dismissed when phase or cycle changes
  const phaseSig = `${phase.cat}-${tipCycle}`;
  useEffect(() => setTipDismissed(false), [phaseSig]);

  return (
    <>
      <div className="bg-gradient-to-b from-navy-900 to-navy-800 text-white px-5 pt-5 pb-3 rounded-t-3xl relative overflow-hidden">
        {/* Gold hairline at the very top edge — 1px, 40% opacity.
            Reads as a card edge, like the trim on a luxury menu card. */}
        <div
          className="absolute top-0 left-0 right-0 h-px bg-gold-500/40 pointer-events-none"
          aria-hidden
        />
        {/* Faint noise texture overlay — kills the plastic sheen of a
            flat gradient. ~4% opacity SVG fractal noise, encoded inline
            to avoid an asset round-trip. Doesn't intercept clicks. */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-soft-light"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='4'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
          aria-hidden
        />
        <ModalCloseButton onClose={closeModal} variant="dark" />

        {/* Cut name + tappable client chip on one line. Phone removed —
            tap the client name/avatar to open the full profile in a nested
            modal where phone + text drafts live. */}
        <div className="flex items-start justify-between gap-3 pr-8">
          <div className="font-display text-2xl leading-tight min-w-0">{cutName}</div>
          {client?.kind === 'existing' && (
            <button
              type="button"
              onClick={() => setClientDetailOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-full pl-1 pr-2.5 py-1 transition-colors"
              aria-label={t('cut.live.openProfile')}
            >
              <Avatar
                name={`${client.firstName} ${client.lastName}`.trim() || t('cut.live.clientFallback')}
                size="sm"
                photoUrl={photoUrls.data?.[client.id]}
              />
              <span className="text-xs font-bold truncate max-w-[120px]">
                {`${client.firstName} ${client.lastName}`.trim() || t('cut.live.clientFallback')}
              </span>
            </button>
          )}
        </div>

        {/* Dual-ring timepiece: outer cream ring tracks the TOTAL cut,
            inner gold ring tracks the CURRENT PHASE. The big readout
            inside the circle shows phase elapsed / phase target so it
            reads at a glance; the total-cut readout sits below the
            ring so it doesn't crowd the inner text. */}
        <PhaseRing
          elapsedSec={phaseElapsed}
          targetSec={phaseTarget}
          paused={paused}
          totalElapsedSec={totalElapsed}
          totalTargetSec={totalTarget}
        />
        {/* TOTAL readout — sits just under the ring. Moved out from
            inside the circle so the phase numbers inside have more
            breathing room and read clearly even at a glance. */}
        <div className="-mt-1 text-center text-[10px] font-bold tracking-[0.18em] uppercase text-white/55 tabular-nums">
          total{' '}
          <span className="text-white/85">
            {fmtMMSS(totalElapsed)} / {fmtMMSS(totalTarget)}
          </span>
        </div>

        {/* Phase rail — hairline-underline style (no chip backgrounds).
            Active phase gets a 2px gold underline; completed phases get
            a faint cream underline; pending phases sit on white/40. Far
            quieter than the old filled-chip row so the dual-ring composition
            above carries the visual story.

            Each phase still tappable to switch — generous vertical padding
            keeps the hit target comfortable despite the lighter look. */}
        <div className="flex gap-3 mt-4 px-1 overflow-x-auto -mx-2 px-2">
          {cutType.phases.map((p, i) => {
            const isCurrent = i === currentPhaseIdx;
            const elapsed = getPhaseElapsedSec(stateSnapshot, i);
            const isDone = !isCurrent && elapsed > 0;
            const timeLabel = isCurrent
              ? `${fmtMMSS(elapsed)} / ${fmtMMSS(p.sec)}`
              : isDone
              ? fmtMMSS(elapsed)
              : fmtMMSS(p.sec);
            const nameColor = isCurrent
              ? 'text-gold-400'
              : isDone
              ? 'text-cream-100/80'
              : 'text-white/45';
            const underline = isCurrent
              ? 'bg-gold-500'
              : isDone
              ? 'bg-cream-100/40'
              : 'bg-white/10';
            return (
              <button
                key={i}
                type="button"
                onClick={() => switchToPhase(i)}
                className="flex-1 min-w-0 pt-2 pb-1.5 text-[11px] font-bold transition-all"
              >
                <div
                  className={`leading-tight line-clamp-2 min-h-[28px] tracking-wide ${nameColor}`}
                >
                  {p.nameKey ? t(p.nameKey) : p.name}
                </div>
                <div
                  className={`text-[10px] mt-1 tabular-nums ${
                    isCurrent ? 'text-gold-400/80' : 'text-white/45'
                  }`}
                >
                  {timeLabel}
                </div>
                <div
                  className={`mt-1.5 h-[2px] rounded-full transition-colors ${underline}`}
                />
              </button>
            );
          })}
        </div>
        {/* Coaching card — cycles between the static Pro Tip and any
            personal Busy Insights every 10s in a single slot. Conserves
            vertical space vs. stacking, and the alternation keeps both
            kinds of help in the barber's eyeline. */}
        {!tipDismissed && (
          <CyclingCoachCard
            phaseName={phaseName}
            tip={tip}
            cutTypeName={cutType.name}
            clientId={client?.kind === 'existing' ? client.id : null}
            targetSec={totalTarget}
            onDismiss={() => setTipDismissed(true)}
          />
        )}

        {/* Added addons chip row */}
        {addedAddons.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {addedAddons.map((a) => {
              const isLocked = currentPhaseIdx >= a.startIdx;
              const addonName = a.nameKey ? t(a.nameKey) : a.name;
              return (
                <span
                  key={a.id}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                    isLocked ? 'bg-green-700/30 text-green-200 opacity-60' : 'bg-green-600/30 text-green-100'
                  }`}
                >
                  + {addonName}
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={() => removeAddon(a.id)}
                      className="ml-1 text-white/70 hover:text-white"
                      aria-label={t('cut.live.removeAddon', { name: addonName })}
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {/* Addon button — single-line, compact. Subtitle dropped; button
            label is descriptive enough on its own. */}
        <button
          type="button"
          onClick={() => setShowAddonPicker(true)}
          className="w-full mt-3 bg-gradient-to-br from-green-600 to-green-700 text-white rounded-xl px-4 h-11 flex items-center gap-2.5 shadow-md shadow-green-700/30 hover:from-green-500 hover:to-green-600 transition-colors"
        >
          <PlusIcon className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left font-extrabold text-sm">
            {t('cut.live.suggestAddon')}
          </span>
          <ChevronRight />
        </button>
      </div>

      {/* Action bar — continues the navy gradient with a frosted-glass
          finish so the timer reads as one carved object instead of three
          stacked panels. A faint gold hairline at the top edge separates
          it from the timer face above (echoes the modal's gold top edge).

          Buttons are full-pill (rounded-full):
            - Pause: low-contrast outline pill (cream stroke) — secondary
            - End cut: solid gold gradient pill with inner highlight —
              primary, the action you actually want the barber to take
              when the cut is finished. */}
      <div className="bg-gradient-to-b from-navy-800 to-navy-900 backdrop-blur-sm px-5 py-4 flex gap-3 relative">
        <div
          className="absolute top-0 left-0 right-0 h-px bg-gold-500/25 pointer-events-none"
          aria-hidden
        />
        <button
          type="button"
          onClick={togglePause}
          className="flex-1 h-12 rounded-full border border-cream-100/40 text-cream-100/90 font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
        >
          {paused ? <PlayIcon className="w-4 h-4" /> : <PauseIcon className="w-4 h-4" />}
          {paused ? t('cut.live.resume') : t('cut.live.pause')}
        </button>
        <button
          type="button"
          onClick={endCut}
          className="flex-1 h-12 rounded-full bg-gradient-to-b from-gold-400 to-gold-500 text-navy-900 font-extrabold inline-flex items-center justify-center gap-2 shadow-lg shadow-gold-500/25 relative overflow-hidden"
        >
          {/* Inner highlight — soft white shine across the top half of
              the pill. Sells the "polished gold" feel. */}
          <span
            className="absolute inset-0 pointer-events-none rounded-full"
            style={{
              background:
                'linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(255,255,255,0) 55%)',
            }}
            aria-hidden
          />
          <StopIcon className="w-4 h-4 relative" />
          <span className="relative">{t('cut.live.endCut')}</span>
        </button>
      </div>

      {showAddonPicker && <AddonPicker onClose={() => setShowAddonPicker(false)} />}

      {/* Nested client profile — opens on top of the live timer when the
          name/avatar chip is tapped. Cut state stays running underneath
          (Zustand-backed); closing the profile drops back into the timer. */}
      {clientDetailOpen && client?.kind === 'existing' && (
        <NestedClientDetail
          clientId={client.id}
          onClose={() => setClientDetailOpen(false)}
        />
      )}
    </>
  );
}

// Local PhaseRing — stays inline since it depends on the live ticker context
// Cycles the Pro Tip ↔ Busy Insight in a single card slot every 10s. When no
// insight is available for the current context, the slot stays on the tip
// permanently. Reserves the same vertical space whether one or both are
// rendered, so the End Cut button below stays predictable on screen.
const CYCLE_MS = 10_000;

function CyclingCoachCard({
  phaseName,
  tip,
  cutTypeName,
  clientId,
  targetSec,
  onDismiss,
}: {
  phaseName: string;
  tip: string;
  cutTypeName: string;
  clientId: string | null;
  targetSec: number;
  onDismiss: () => void;
}) {
  const t = useT();
  const visits = useRecentVisits(90);
  const insights = useMemoInsights(visits.data ?? null, cutTypeName, clientId, targetSec);
  const [showingTip, setShowingTip] = useState(true);
  // Track which insight (when more than one available) — cycle through them
  // along with the tip on a single rotation.
  const [insightIdx, setInsightIdx] = useState(0);

  useEffect(() => {
    if (insights.length === 0) return; // no insights → no cycling
    const id = setInterval(() => {
      setShowingTip((s) => {
        const nextShowingTip = !s;
        if (nextShowingTip === false) {
          // We're rotating to insight — pick next one.
          setInsightIdx((i) => (i + 1) % insights.length);
        }
        return nextShowingTip;
      });
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [insights.length]);

  // When the phase changes, reset to the tip (relevant for the new phase).
  useEffect(() => {
    setShowingTip(true);
  }, [phaseName]);

  const onTip = showingTip || insights.length === 0;
  const insight = insights[insightIdx];
  const eyebrow = onTip ? `${t('cut.live.proTip')} · ${phaseName}` : t('cut.insight.eyebrow');
  const body = onTip ? tip : insight ? t(insight.key, insight.vars) : '';

  return (
    <button
      type="button"
      onClick={onDismiss}
      className={`w-full mt-4 text-left rounded-xl p-4 flex items-start gap-3 shadow-lg transition-colors relative overflow-hidden ${
        onTip
          ? 'bg-gradient-to-br from-gold-500 to-gold-400 text-navy-900 shadow-gold-500/20'
          : 'bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-navy-900/30 border border-gold-500/30'
      }`}
    >
      {/* Gold hairline along the top edge — concierge-card detail. On
          the gold-tip variant the line is a deeper navy hairline for
          contrast; on the navy-insight variant it's a gold hairline. */}
      <span
        className={`absolute top-0 left-0 right-0 h-px pointer-events-none ${
          onTip ? 'bg-navy-900/30' : 'bg-gold-500/60'
        }`}
        aria-hidden
      />
      <div
        className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
          onTip ? 'bg-navy-900/15' : 'bg-gold-500/15 text-gold-400'
        }`}
      >
        {onTip ? <LightbulbIcon className="w-4 h-4" /> : <ChartIcon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-[10px] font-bold tracking-[0.14em] uppercase ${
            onTip ? 'text-navy-700' : 'text-gold-400'
          }`}
        >
          {eyebrow}
        </div>
        <div className="text-[13.5px] font-semibold mt-1 leading-snug">{body}</div>
        {insights.length > 0 && (
          <div className="flex items-center gap-1 mt-2">
            {[true, false].map((isTipDot, i) => {
              const active = isTipDot ? onTip : !onTip;
              return (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    active ? (onTip ? 'bg-navy-900/70' : 'bg-gold-400') : (onTip ? 'bg-navy-900/20' : 'bg-white/30')
                  }`}
                />
              );
            })}
          </div>
        )}
      </div>
      <DismissIcon className={`w-4 h-4 shrink-0 ${onTip ? 'text-navy-700' : 'text-white/60'}`} />
    </button>
  );
}

// Compute insights inline using the existing computeCutInsights helper —
// avoids the InsightCard's separate render lifecycle so the cycler can
// own the slot exclusively.
function useMemoInsights(
  visits: VisitRow[] | null,
  cutTypeName: string,
  clientId: string | null,
  targetSec: number,
) {
  return useMemo(() => {
    if (!visits) return [] as ReturnType<typeof computeCutInsights>;
    return computeCutInsights({
      cutTypeName,
      clientId,
      targetSec,
      recentVisits: visits.map((v) => ({
        client_id: v.client_id,
        cut_type: v.cut_type,
        duration_min: v.duration_min,
        started_at: v.started_at,
        price_cents: v.price_cents,
      })),
    });
  }, [visits, cutTypeName, clientId, targetSec]);
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </svg>
  );
}

/**
 * Dual-ring PhaseRing — "Velvet Concierge" finish.
 *
 *   Outer ring  cream-100, ~75% opacity — TOTAL cut progress
 *   Bezel       1px gold hairline between the two rings
 *   Inner ring  gold conic gradient — CURRENT PHASE progress
 *
 * Hierarchy comes from saturation (inner is the live element, outer
 * is the chapter ring), not stroke width — both rings use the same
 * stroke weight, just different radii. References the classic
 * luxury-watch chronograph composition (cream dial with gold hands).
 *
 * Overrun: the INNER ring flips to red. The outer ring stays cream —
 * the total cut isn't necessarily over budget when one phase overruns.
 * Pause: the inner ring's gold gently pulses (CSS keyframes via the
 * surrounding wrapper class).
 */
function PhaseRing({
  elapsedSec,
  targetSec,
  paused,
  totalElapsedSec,
  totalTargetSec,
}: {
  elapsedSec: number;
  targetSec: number;
  paused: boolean;
  totalElapsedSec: number;
  totalTargetSec: number;
}) {
  // SVG geometry (100 viewBox units). Both rings 7-unit stroke, gap
  // between strokes is the 2-unit "bezel band" where the hairline sits.
  const OUTER_R = 46;
  const INNER_R = 35;
  const BEZEL_R = 40.5; // halfway between outer & inner inside edges
  const STROKE = 7;
  const OUTER_CIRC = 2 * Math.PI * OUTER_R;
  const INNER_CIRC = 2 * Math.PI * INNER_R;

  const isOver = elapsedSec > targetSec && !paused;
  const phaseProgress = targetSec > 0 ? Math.min(elapsedSec / targetSec, 1) : 0;
  const totalProgress =
    totalTargetSec > 0 ? Math.min(totalElapsedSec / totalTargetSec, 1) : 0;

  const innerOffset = INNER_CIRC * (1 - phaseProgress);
  const outerOffset = OUTER_CIRC * (1 - totalProgress);

  // Inner stroke color: red on overrun, gold gradient (via SVG <defs>)
  // otherwise. Paused state desaturates slightly via opacity.
  const innerStroke = isOver ? '#c8102e' : 'url(#liveTimer-goldArc)';
  // Big number color: warm cream on normal, red on overrun, gold when
  // paused. Replaces the old green which clashed with the cream chapter ring.
  const numberColor = paused
    ? 'text-gold-400'
    : isOver
    ? 'text-red-400'
    : 'text-cream-100';

  // Haptic feedback when crossing into the red — wakes the barber's attention
  // without a sound. Stronger pulse at 30s and 60s past target so a long
  // overrun keeps cueing them. Web Vibration API: works on Android Chrome,
  // no-op on iOS Safari (Capacitor will fix that in v1.1).
  const wasOverRef = useRef(false);
  const lastEscalationRef = useRef<number>(0);
  useEffect(() => {
    if (!isOver) {
      wasOverRef.current = false;
      lastEscalationRef.current = 0;
      return;
    }
    if (!wasOverRef.current) {
      pulseHaptic('warning');
      wasOverRef.current = true;
      return;
    }
    const overBy = elapsedSec - targetSec;
    const bucket = overBy >= 60 ? 60 : overBy >= 30 ? 30 : 0;
    if (bucket > 0 && bucket > lastEscalationRef.current) {
      pulseHaptic('heavy');
      lastEscalationRef.current = bucket;
    }
  }, [isOver, elapsedSec, targetSec]);

  return (
    <div
      className={`relative w-[210px] h-[210px] mx-auto mt-3 mb-2 ${
        paused ? 'opacity-90' : ''
      }`}
    >
      {/* Soft radial vignette behind the ring — pulls the eye to center.
          Sits in absolute z-0; rings + number stack on top. */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, rgba(212,169,66,0.10) 0%, rgba(0,0,0,0.0) 55%, rgba(0,0,0,0.25) 100%)',
        }}
        aria-hidden
      />
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90 relative">
        <defs>
          {/* Conic-ish gold gradient on the inner phase ring. SVG
              doesn't natively support conic gradients on strokes, but
              a linear gradient on a -rotate-90'd ring approximates a
              warm "ramp" across the arc — light gold start, deep gold
              end. Reads as live/saturated against the cream outer. */}
          <linearGradient id="liveTimer-goldArc" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f3dc99" />
            <stop offset="55%" stopColor="#e8c66a" />
            <stop offset="100%" stopColor="#d4a942" />
          </linearGradient>
        </defs>

        {/* Outer ring track — cream chapter ring at low opacity */}
        <circle
          cx="50"
          cy="50"
          r={OUTER_R}
          fill="none"
          stroke="rgba(240, 230, 207, 0.12)"
          strokeWidth={STROKE}
        />
        {/* Outer ring progress — TOTAL cut. Cream at ~75% opacity.
            Slower transition (1s) so the chapter ring reads as a quiet
            background element vs the snappier 400ms inner ring. */}
        <circle
          cx="50"
          cy="50"
          r={OUTER_R}
          fill="none"
          stroke="rgba(240, 230, 207, 0.75)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={OUTER_CIRC}
          strokeDashoffset={outerOffset}
          style={{ transition: 'stroke-dashoffset 1000ms linear' }}
        />

        {/* Gold bezel hairline — the visual divider between chapter ring
            and phase ring. Sits in the gap between the two strokes. */}
        <circle
          cx="50"
          cy="50"
          r={BEZEL_R}
          fill="none"
          stroke="rgba(212, 169, 66, 0.55)"
          strokeWidth="0.4"
        />

        {/* Inner ring track — faint navy-lighter to register the ring shape */}
        <circle
          cx="50"
          cy="50"
          r={INNER_R}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={STROKE}
        />
        {/* Inner ring progress — CURRENT PHASE. Gold gradient by default,
            red on overrun. The live element; faster transition so motion
            feels responsive. */}
        <circle
          cx="50"
          cy="50"
          r={INNER_R}
          fill="none"
          stroke={innerStroke}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={INNER_CIRC}
          strokeDashoffset={innerOffset}
          style={{ transition: 'stroke-dashoffset 400ms linear, stroke 200ms ease' }}
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div
          className={`font-display text-[44px] leading-none tabular-nums tracking-tight ${numberColor}`}
        >
          {fmtMMSS(elapsedSec)}
        </div>
        {/* Single-line phase readout — "of 2:00 PHASE". Compact small
            caps with wide tracking; cream so it ties to the chapter
            ring. The total-cut readout moved out below the ring (see
            LiveTimer) so the inside of the circle reads at a glance
            with breathing room around the big number. */}
        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/65 mt-2.5 text-center">
          of <strong className="text-cream-100/95">{fmtMMSS(targetSec)}</strong> phase
        </div>
      </div>
    </div>
  );
}

/* ================ icons ================ */
function LightbulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2z" />
    </svg>
  );
}
function DismissIcon({ className }: { className?: string }) {
  return <XIcon className={className} />;
}
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}
function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="6 4 20 12 6 20" />
    </svg>
  );
}
function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/70" aria-hidden>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

// Wraps ClientDetailModal with the lookup logic so the LiveTimer parent
// doesn't have to fetch clients/stats/tiers when the profile isn't open.
// Hooks only fire when this component mounts (i.e. user tapped the chip).
function NestedClientDetail({
  clientId,
  onClose,
}: {
  clientId: string;
  onClose: () => void;
}) {
  const clients = useClients();
  const stats = useClientStats();
  const tiers = useClientTiers();
  const client = (clients.data ?? []).find((c) => c.id === clientId);
  if (!client) return null;
  const cs = (stats.data ?? []).find((s) => s.client_id === clientId);
  const ct = (tiers.data ?? []).find((row) => row.client_id === clientId);
  return (
    <ClientDetailModal
      client={client}
      stats={cs}
      tier={ct?.tier}
      onClose={onClose}
    />
  );
}
